// TES-899 part 3 — the coach's check-in list: which check-ins still need her,
// which are history, and the one line each row says about itself (week, full
// submission date, status). Pure, so it runs under `node --test`.

import { MEDICAL_REVIEW_NEUTRAL_STATUS } from "@care-by-ella/contracts";
import { CHECKIN_STATE_LABEL } from "./coach-now";

export type CheckinListItem = {
  id: string;
  // TES-1561 — "milestone" for a monthly check-in (F-05); absent is F-04.
  templateKey?: string;
  // TES-1561 (Ö-865, Ö-866) — hers to answer, also when routed to the
  // doctor. False only for a check-in routed before the cutoff.
  coachCase?: boolean;
  submittedAt: string;
  flags: { rule: string }[];
  // The coach's pre-TES-899 "granskad" stamp.
  reviewedAt: string | null;
  medicalStatus?: string | null;
  coachHandling?: {
    state: "open" | "answered" | "closed_without_reply";
    handledAt: string | null;
    // TES-1210 — who handled it, and whether an open one has a saved draft.
    handledByName?: string | null;
    draftSaved?: boolean;
  };
};

export type CheckinStatus =
  | { kind: "open" }
  // TES-1210 — still open, with a reply draft generated and not yet sent.
  | { kind: "draft_saved" }
  | { kind: "answered"; at: string | null; by?: string | null }
  | { kind: "closed_without_reply"; at: string | null }
  // Stamped "granskad" before the handling state existed (no review row):
  // handled, but with nothing recorded about how.
  | { kind: "reviewed"; at: string }
  // Routed to the doctor; not hers to handle.
  | { kind: "medical" };

const TZ = "Europe/Stockholm";

export function checkinStatus(c: CheckinListItem): CheckinStatus {
  // TES-1561 — a routed check-in that is also her case is handled like any
  // other; its `medicalStatus` is a note beside her part, not its status.
  if (c.medicalStatus && c.coachCase !== true) return { kind: "medical" };
  const h = c.coachHandling;
  if (h?.state === "answered") {
    return { kind: "answered", at: h.handledAt, by: h.handledByName ?? null };
  }
  if (h?.state === "closed_without_reply") {
    return { kind: "closed_without_reply", at: h.handledAt };
  }
  if (c.reviewedAt) return { kind: "reviewed", at: c.reviewedAt };
  return h?.draftSaved ? { kind: "draft_saved" } : { kind: "open" };
}

/** Still hers to handle: open, with or without a saved draft. */
export function isOpenCheckin(s: CheckinStatus): boolean {
  return s.kind === "open" || s.kind === "draft_saved";
}

export function splitCheckins<T extends CheckinListItem>(
  checkins: T[],
): { needsHandling: T[]; earlier: T[] } {
  const newest = (a: T, b: T) => b.submittedAt.localeCompare(a.submittedAt);
  const open = checkins.filter((c) => isOpenCheckin(checkinStatus(c)));
  return {
    // Flagged first (that is what the queue sent her), then newest.
    needsHandling: open.sort(
      (a, b) =>
        Number(b.flags.length > 0) - Number(a.flags.length > 0) || newest(a, b),
    ),
    earlier: checkins
      .filter((c) => !isOpenCheckin(checkinStatus(c)))
      .sort(newest),
  };
}

// ISO 8601 week of the Swedish calendar date the instant falls on.
export function isoWeek(iso: string): number {
  const [y, m, d] = new Intl.DateTimeFormat("sv-SE", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(new Date(iso))
    .split("-")
    .map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  // Thursday of this week decides the year the week belongs to.
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.ceil(((date.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

/** "29 september 2026 kl. 12:00" */
export function fullDate(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    timeZone: TZ,
    dateStyle: "long",
    timeStyle: "short",
  });
}

/** "2026-09-29 12:00" */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    timeZone: TZ,
    dateStyle: "short",
    timeStyle: "short",
  });
}

// TES-1210 — A-01: "Statusar är minst: Behöver svar; Utkast sparat;
// Besvarad; Avslutad utan svar. En besvarad rad visar tydligt 'Svar skickat',
// tidpunkt och coach." The words come from the one table Aktuellt also uses
// (CHECKIN_STATE_LABEL), so the list and Aktuellt cannot say two different
// things about the same check-in.
export const CHECKIN_DRAFT_SAVED_LABEL = "Utkast sparat";

export function checkinStatusLabel(s: CheckinStatus): string {
  const at = (sep: string, iso: string | null) =>
    iso ? `${sep}${shortDate(iso)}` : "";
  switch (s.kind) {
    case "open":
      return CHECKIN_STATE_LABEL.open;
    case "draft_saved":
      return CHECKIN_DRAFT_SAVED_LABEL;
    case "answered":
      return `${CHECKIN_STATE_LABEL.answered} · Svar skickat${at(" ", s.at)}${s.by ? ` av ${s.by}` : ""}`;
    case "closed_without_reply":
      return `${CHECKIN_STATE_LABEL.closed_without_reply}${at(" · ", s.at)}`;
    case "reviewed":
      return `Granskad · ${shortDate(s.at)}`;
    // TES-1213 — A-01's one neutral sentence, the same in every coach view.
    case "medical":
      return MEDICAL_REVIEW_NEUTRAL_STATUS;
  }
}

/** "Veckoincheckning · vecka 40 · Inlämnad 29 september 2026 kl. 12:00" */
export function checkinRowTitle(c: {
  submittedAt: string;
  templateKey?: string;
}): string {
  // TES-1561 — "Månadsincheckning" is Emelie's word for F-05 in Ö-866.
  const name =
    c.templateKey === "milestone" ? "Månadsincheckning" : "Veckoincheckning";
  return `${name} · vecka ${isoWeek(c.submittedAt)} · Inlämnad ${fullDate(c.submittedAt)}`;
}
